import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from 'shared/api';
import { useExecWithDebounce } from 'shared/hooks/useExecWithDebounce';

const DEFAULT_MIN_LENGTH = 2;
const DEFAULT_DEBOUNCE_MS = 300;

export interface UseServerSearchOptions<T> {
  minLength?: number;
  fetcher: (query: string, signal: AbortSignal) => Promise<ApiError | T[]>;
  preloadOnMount?: boolean;
}

export function useServerSearch<T>(
    query: string,
    {
        minLength = DEFAULT_MIN_LENGTH,
        fetcher,
        preloadOnMount = false
    }: UseServerSearchOptions<T>
) {
    const [ options, setOptions ] = useState<T[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);

    const abortRef = useRef<AbortController | null>(null);
    const didPreloadRef = useRef(false);

    const execWithDebounce = useExecWithDebounce({ delay: DEFAULT_DEBOUNCE_MS });

    const abort = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
    }, []);

    const startFetch = useCallback(
        async (query: string) => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            setLoading(true);
            setError(null);

            // A request is stale once it has been aborted or superseded by a newer one.
            // Stale results (including cancellations, which surface as ApiError/CanceledError
            // rather than AbortError) must be ignored, not shown as a fetch error.
            const isStale = () => controller.signal.aborted || abortRef.current !== controller;

            try {
                const data = await fetcher(query, controller.signal);

                if (isStale()) {
                    return;
                }

                if (data instanceof ApiError) {
                    setOptions([]);
                    setError(data.message?.length ? data.message : 'Unknown error');
                } else {
                    setOptions(data);
                }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: any) {
                if (!isStale() && e?.name !== 'AbortError') {
                    setOptions([]);
                    setError(e?.message ?? 'Unknown error');
                }
            } finally {
                if (abortRef.current === controller) {
                    setLoading(false);
                }
            }
        },
        [ fetcher ]
    );

    const prefetch = useCallback(() => {
        if (!didPreloadRef.current) {
            didPreloadRef.current = true;
            startFetch('');
        }
    }, [ startFetch ]);

    useEffect(function debouncedSearch() {
        const q = query.trim();
        const canSearch = q.length >= minLength;

        if (!canSearch) {
            setLoading(false);
            setError(null);

            return;
        }

        execWithDebounce(() => startFetch(q));

        return () => abort();
    }, [ query, minLength, startFetch, abort, execWithDebounce ]);

    useEffect(function initialPreloadOnMount(){
        if (preloadOnMount && !query.trim().length) {
            prefetch();
        }
    }, [ preloadOnMount, query, prefetch ]);

    return { options, loading, error, prefetch };
}
